import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { hero } from "@/data/siteContent";
import { scrollToSection } from "@/hooks/useLenis";
import PrimaryCTA from "@/components/PrimaryCTA";
import { ArrowDownRight } from "lucide-react";
import useHeroAnimation from "./hooks/useHeroAnimation";
import { heroConfig } from "./config/heroConfig";
import { pointerToCssParallax } from "./utils/mouseMath";

/**
 * Editorial DOM layer: copy, CTAs, stats, CSS photo backdrop.
 * GSAP entrance + ScrollTrigger parallax live here (not in R3F).
 * `animationRootRef` should be the Hero <section> for correct ScrollTrigger bounds.
 */
export default function HeroContent({
  ready,
  showPhotoBackdrop = true,
  animationRootRef,
}) {
  const bgRef = useRef(null);
  useHeroAnimation(animationRootRef, { ready, bgRef });

  useEffect(() => {
    if (prefersReducedMotion() || !showPhotoBackdrop) return undefined;
    const { strength, duration } = heroConfig.mouse.cssParallax;
    const onMove = (e) => {
      if (!bgRef.current) return;
      const { x, y } = pointerToCssParallax(e.clientX, e.clientY, strength);
      gsap.to(bgRef.current, { x, y, duration, ease: "power3.out" });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [showPhotoBackdrop]);

  return (
    <>
      {showPhotoBackdrop ? (
        <div
          ref={bgRef}
          className="absolute inset-[-10%] z-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${hero.backgroundImage})` }}
          aria-hidden
        />
      ) : (
        <div ref={bgRef} className="absolute inset-0 z-0 bg-[#050505]" aria-hidden />
      )}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/55 via-black/25 to-[#050505]" />
      <div className="absolute inset-0 z-0 bg-black/10" />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-10">
        <div data-hero-fade className="mb-7 flex items-center gap-3">
          <span className="h-px w-10 bg-gold/70" />
          <p
            className="font-accent text-base italic tracking-wide text-white/75"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {hero.eyebrow}
          </p>
        </div>

        <h1
          className="max-w-5xl font-display text-white"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 600 }}
        >
          {hero.headlineLines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.09em]">
              <span
                data-hero-line
                className="block text-[13.5vw] leading-[0.92] tracking-[-0.02em] sm:text-[11vw] lg:text-[9vw]"
              >
                {line}
              </span>
            </span>
          ))}
        </h1>

        <div className="mt-14 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <p
            data-hero-fade
            className="max-w-md font-body text-base leading-[1.7] text-white/75 lg:text-lg"
          >
            {hero.subcopy}
          </p>

          <div data-hero-fade className="flex flex-wrap items-center gap-4">
            <PrimaryCTA
              as="button"
              onClick={() => scrollToSection(hero.primaryCta.href)}
              className="px-7 py-4"
              data-testid="hero-primary-cta"
            >
              {hero.primaryCta.label}
              <ArrowDownRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5"
              />
            </PrimaryCTA>
            <button
              type="button"
              onClick={() => scrollToSection(hero.secondaryCta.href)}
              className="rounded-full border border-white/20 px-7 py-4 text-sm text-white transition-colors hover:border-gold/60 hover:bg-white/5"
              data-testid="hero-secondary-cta"
            >
              {hero.secondaryCta.label}
            </button>
          </div>
        </div>

        <div data-hero-fade className="mt-14 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
          {hero.stats.map((s) => (
            <div key={s.label}>
              <div
                className="font-display text-3xl text-white sm:text-4xl"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 600 }}
              >
                {s.value}
                <span className="text-gold/80">{s.suffix}</span>
              </div>
              <div className="mt-1.5 text-xs uppercase tracking-[0.15em] text-white/60">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
